import { useEffect, useMemo, useState } from "react";
import {
  CalendarRange,
  Download,
  FileText,
  LockKeyhole,
  Plus,
  Save,
  SquarePen,
  Table2,
  Trash2,
  WalletCards,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import AttendanceWorkspace from "../attendance/AttendanceWorkspace";

const LANG = {
  zh: {
    title: "工资明细",
    subtitle: "员工工资基础资料维护与每月給与明細确认分开管理",
    badge: "工资管理",
    profileMode: "员工资料",
    payslipMode: "工资台账",
    profileTitle: "员工工资档案编辑页",
    profileSubtitle: "用于维护员工工资计算基础资料，不作为正式給与明細使用。",
    payslipTitle: "员工年度与月度工资台账",
    payslipSubtitle: "每位员工都有年度汇总表和月度明细表，月度保存后年度数据立即联动。",
    annualView: "年度工资表",
    monthlyView: "月度明细",
    yearLabel: "年度",
    annualTotal: "年度合计",
    selectEmployee: "选择员工",
    generateMonth: "创建/补写该月明细",
    noMonthlyPayslip: "该月尚未生成工资明细",
    backToAnnual: "返回年度表",
    historyMonth: "补写年月",
    openHistoryMonth: "打开/补写月份",
    historyMonthHint: "可选择任意以前的年月，已有明细会直接打开，没有明细则可创建。",
    paymentDateHint: "支給日可以自行设定，保存后会用于 PDF 和年度记录。",
    officialCompanyFixed: "公司资料和正式印章已固定",
    taxablePayGroup: "课税支给",
    nonTaxablePayGroup: "非课税支给",
    adjustmentGroup: "其他调整",
    attendanceGroup: "勤怠",
    taxablePayTotal: "课税支给合计",
    nonTaxablePayTotal: "非课税支给合计",
    add: "新增档案",
    refresh: "刷新批次",
    createPayslip: "給与明細を作成",
    companySettings: "公司信息与印章",
    saveCompanySettings: "保存公司信息",
    companyName: "会社名",
    companyAddress: "会社住所",
    companyPhone: "电话",
    representativeName: "代表者",
    stampText: "印章文字",
    editPayslip: "编辑明细",
    savePayslip: "保存給与明細",
    taxable: "课税",
    nonTaxable: "非课税",
    addEarning: "添加支給项目",
    addDeduction: "添加控除项目",
    pdfOutput: "PDF出力",
    batchPdfOutput: "PDF批量导出",
    confirmPayslip: "確定",
    revisePayslip: "修正",
    correcting: "修正中",
    excelOutput: "Excel出力",
    statsEmployees: "员工档案",
    statsRuns: "本月批次",
    statsPayslips: "明细数量",
    statsNet: "实发合计",
    employeeList: "员工工资档案",
    employee: "员工",
    type: "类型",
    baseSalary: "基本工资",
    standardMonthlyShort: "标准报酬",
    allowance: "固定手当",
    status: "状态",
    actions: "操作",
    active: "生成对象",
    inactive: "停用",
    edit: "编辑",
    emptyEmployees: "暂无员工工资档案",
    batch: "給与明細批次",
    batchHint: "选择月份后生成草稿，复核后確定并输出 PDF。",
    selectBatch: "选择批次",
    locked: "已確定",
    draft: "草稿",
    noPayslips: "请选择或生成給与明細草稿",
    modalTitleEdit: "编辑员工工资档案",
    modalTitleCreate: "新增员工工资档案",
    modalSubtitle: "这里只维护工资计算基础资料。標準報酬月額用于社会保险计算，不等同于基本工资。",
    langLabel: "语言",
    sourceSetup: "资料来源",
    manualEmployeeMode: "手动新增员工",
    driverImportMode: "从在职司机导入",
    driverImportHint: "仅显示在职司机。选择后会自动带入司机姓名、手机号和账号关联。",
    selectDriver: "选择在职司机",
    selectDriverPlaceholder: "请选择司机",
    noActiveDrivers: "暂无可导入的在职司机",
    basicInfo: "员工基本信息",
    salarySetup: "工资基础设置",
    taxInsurance: "税务与保险设置",
    allowanceNotes: "手当与备注",
    name: "员工姓名",
    phone: "手机号",
    employeeType: "员工类型",
    taxCategory: "税区分",
    taxKou: "甲栏",
    taxOtsu: "乙栏",
    taxCategoryHint: "甲栏：已向本公司提交扶养控除等申告书；乙栏：未提交或本公司是副职。",
    employmentCategory: "雇用保险类别",
    generalBusiness: "一般事业",
    transportBusiness: "运输相关",
    constructionBusiness: "建设事业",
    staff: "员工",
    driver: "司机",
    dispatcher: "调度",
    internal: "内部员工",
    admin: "管理员",
    standardMonthly: "标准报酬月额",
    standardHint: "用于社会保险料计算，请根据日本社会保险标准报酬月额表填写。",
    residentTaxHint: "住民税不从月工资扣除，每年另行缴纳。",
    dependents: "扶养人数",
    prefecture: "保险地区",
    careInsurance: "介护保险对象",
    careHint: "原则上 40 岁以上 65 岁未满为对象。没有生年月日资料时，请在员工资料中手动确认。",
    payrollTarget: "工资生成对象",
    payrollTargetHint: "开启后，该员工会进入月度給与明細草稿生成范围。",
    allowanceName: "手当名称",
    amount: "金额",
    delete: "删除",
    addAllowance: "添加固定手当",
    notes: "备注",
    cancel: "取消",
    save: "保存员工资料",
    required: "必填",
    incomeTax: "源泉所得税",
    healthInsurance: "健康保険",
    pensionInsurance: "厚生年金",
    employmentInsurance: "雇用保険",
    workersInsurance: "労災保険",
    careInsuranceJa: "介護保険",
    payslipBasicInfo: "基本情報",
    paymentMonth: "支給年月",
    paymentDate: "支給日",
    employeeName: "社員名",
    employeeNo: "社員番号",
    department: "所属",
    workDays: "勤務日数",
    attendanceDays: "出勤日数",
    absenceDays: "欠勤日数",
    workHours: "労働時間",
    overtimeHours: "残業時間",
    earningsSection: "支給項目",
    deductionsSection: "控除項目",
    basePay: "基本給",
    commutingAllowance: "通勤手当",
    roleAllowance: "役職手当",
    overtimeAllowance: "残業手当",
    lateNightAllowance: "深夜手当",
    holidayAllowance: "休日手当",
    otherAllowance: "その他手当",
    residentTax: "住民税",
    otherDeduction: "その他控除",
    grossPay: "総支給額",
    totalDeductions: "控除合計",
    netPay: "差引支給額",
    residentTaxPolicy: "住民税如果不是特别征收，不显示在工资控除项目中。",
  },
  ja: {
    title: "給与明細",
    subtitle: "従業員給与台帳と月次給与明細確認を分けて管理します",
    badge: "給与管理",
    profileMode: "従業員資料",
    payslipMode: "給与台帳",
    profileTitle: "従業員給与台帳編集",
    profileSubtitle: "給与計算の基礎資料を管理する画面です。正式な給与明細ではありません。",
    payslipTitle: "従業員別 年間・月次給与台帳",
    payslipSubtitle: "従業員ごとに年間表と月次明細を管理し、月次保存後に年間表へ即時反映します。",
    annualView: "年間給与表",
    monthlyView: "月次明細",
    yearLabel: "年度",
    annualTotal: "年間合計",
    selectEmployee: "従業員を選択",
    generateMonth: "この月の明細を作成・追記",
    noMonthlyPayslip: "この月の給与明細はまだ作成されていません",
    backToAnnual: "年間表へ戻る",
    historyMonth: "過去月の追記",
    openHistoryMonth: "月を開く・追記",
    historyMonthHint: "過去の年月を自由に選択できます。既存明細は開き、未作成の場合は新規作成できます。",
    paymentDateHint: "支給日は自由に設定でき、保存後は PDF と年間台帳へ反映されます。",
    officialCompanyFixed: "会社情報と正式印章は固定済みです",
    taxablePayGroup: "課税支給",
    nonTaxablePayGroup: "非課税支給",
    adjustmentGroup: "その他調整",
    attendanceGroup: "勤怠",
    taxablePayTotal: "課税支給額合計",
    nonTaxablePayTotal: "非課税支給額合計",
    add: "台帳追加",
    refresh: "月次更新",
    createPayslip: "給与明細を作成",
    companySettings: "会社情報・印章",
    saveCompanySettings: "会社情報を保存",
    companyName: "会社名",
    companyAddress: "会社住所",
    companyPhone: "電話番号",
    representativeName: "代表者",
    stampText: "印章文字",
    editPayslip: "明細編集",
    savePayslip: "給与明細を保存",
    taxable: "課税",
    nonTaxable: "非課税",
    addEarning: "支給項目を追加",
    addDeduction: "控除項目を追加",
    pdfOutput: "PDF出力",
    batchPdfOutput: "PDF一括出力",
    confirmPayslip: "確定",
    revisePayslip: "修正",
    correcting: "修正中",
    excelOutput: "Excel出力",
    statsEmployees: "従業員台帳",
    statsRuns: "当月処理",
    statsPayslips: "明細件数",
    statsNet: "差引支給合計",
    employeeList: "従業員給与台帳",
    employee: "従業員",
    type: "区分",
    baseSalary: "基本給",
    standardMonthlyShort: "標準報酬",
    allowance: "固定手当",
    status: "状態",
    actions: "操作",
    active: "作成対象",
    inactive: "停止",
    edit: "編集",
    emptyEmployees: "従業員給与台帳はまだありません",
    batch: "給与明細処理",
    batchHint: "月を選択して草稿作成後、確認して確定・PDF 出力します。",
    selectBatch: "処理を選択",
    locked: "確定済",
    draft: "草稿",
    noPayslips: "給与明細草稿を選択または作成してください",
    modalTitleEdit: "従業員給与台帳を編集",
    modalTitleCreate: "従業員給与台帳を追加",
    modalSubtitle: "給与計算の基礎資料のみを管理します。標準報酬月額は社会保険料計算用で、基本給とは別です。",
    langLabel: "表示言語",
    sourceSetup: "資料元",
    manualEmployeeMode: "手動で従業員を追加",
    driverImportMode: "在職乗務員から取り込み",
    driverImportHint: "在職乗務員のみ表示します。選択すると氏名、電話番号、アカウント連携を自動入力します。",
    selectDriver: "在職乗務員を選択",
    selectDriverPlaceholder: "乗務員を選択してください",
    noActiveDrivers: "取り込み可能な在職乗務員はいません",
    basicInfo: "従業員基本情報",
    salarySetup: "給与基本設定",
    taxInsurance: "税務・保険設定",
    allowanceNotes: "手当・備考",
    name: "従業員氏名",
    phone: "電話番号",
    employeeType: "従業員区分",
    taxCategory: "税区分",
    taxKou: "甲欄",
    taxOtsu: "乙欄",
    taxCategoryHint: "甲欄：当社へ扶養控除等申告書を提出済み。乙欄：未提出、または当社が従たる勤務先。",
    employmentCategory: "雇用保険区分",
    generalBusiness: "一般事業",
    transportBusiness: "運輸関連",
    constructionBusiness: "建設事業",
    staff: "従業員",
    driver: "乗務員",
    dispatcher: "配車担当",
    internal: "社内担当",
    admin: "管理者",
    standardMonthly: "標準報酬月額",
    standardHint: "社会保険料計算に使用します。日本の社会保険標準報酬月額表に基づいて入力してください。",
    residentTaxHint: "住民税は月次給与から控除しません。年次で別途納付します。",
    dependents: "扶養人数",
    prefecture: "保険地区",
    careInsurance: "介護保険対象",
    careHint: "原則として 40歳以上65歳未満が対象です。生年月日情報がない場合は従業員資料で手動確認してください。",
    payrollTarget: "給与作成対象",
    payrollTargetHint: "オンの場合、月次給与明細草稿の作成対象に含めます。",
    allowanceName: "手当名",
    amount: "金額",
    delete: "削除",
    addAllowance: "固定手当を追加",
    notes: "備考",
    cancel: "キャンセル",
    save: "従業員資料を保存",
    required: "必須",
    incomeTax: "源泉所得税",
    healthInsurance: "健康保険",
    pensionInsurance: "厚生年金",
    employmentInsurance: "雇用保険",
    workersInsurance: "労災保険",
    careInsuranceJa: "介護保険",
    payslipBasicInfo: "基本情報",
    paymentMonth: "支給年月",
    paymentDate: "支給日",
    employeeName: "社員名",
    employeeNo: "社員番号",
    department: "所属",
    workDays: "勤務日数",
    attendanceDays: "出勤日数",
    absenceDays: "欠勤日数",
    workHours: "労働時間",
    overtimeHours: "残業時間",
    earningsSection: "支給項目",
    deductionsSection: "控除項目",
    basePay: "基本給",
    commutingAllowance: "通勤手当",
    roleAllowance: "役職手当",
    overtimeAllowance: "残業手当",
    lateNightAllowance: "深夜手当",
    holidayAllowance: "休日手当",
    otherAllowance: "その他手当",
    residentTax: "住民税",
    otherDeduction: "その他控除",
    grossPay: "総支給額",
    totalDeductions: "控除合計",
    netPay: "差引支給額",
    residentTaxPolicy: "住民税は特別徴収でない場合、給与控除項目に表示しません。",
  },
};

const PREFECTURES = [
  { value: "chiba", label: "千葉県" },
  { value: "tokyo", label: "東京都" },
  { value: "kanagawa", label: "神奈川県" },
  { value: "saitama", label: "埼玉県" },
  { value: "osaka", label: "大阪府" },
];

const EARNING_ROWS = [
  { key: "basePay", labels: ["基本給"] },
  { key: "commutingAllowance", labels: ["通勤手当", "交通費", "交通手当"] },
  { key: "roleAllowance", labels: ["役職手当"] },
  { key: "overtimeAllowance", labels: ["残業手当"] },
  { key: "lateNightAllowance", labels: ["深夜手当"] },
  { key: "holidayAllowance", labels: ["休日手当"] },
];

const DEDUCTION_ROWS = [
  { key: "healthInsurance", labels: ["健康保険"] },
  { key: "pensionInsurance", labels: ["厚生年金"] },
  { key: "careInsuranceJa", labels: ["介護保険"] },
  { key: "employmentInsurance", labels: ["雇用保険"] },
  { key: "incomeTax", labels: ["源泉所得税", "所得税"] },
  { key: "residentTax", labels: ["住民税"], hideWhenZero: true },
];

function formatCurrency(amount) {
  return `¥${Math.round(Number(amount) || 0).toLocaleString()}`;
}

function numberValue(value) {
  const numeric = String(value ?? "").replace(/[^\d.-]/g, "");
  return numeric === "" ? "" : String(Number(numeric));
}

function formatCurrencyInput(value) {
  const numeric = numberValue(value);
  if (numeric === "") return "";
  return `¥ ${Number(numeric).toLocaleString()}`;
}

function parseAllowanceRows(text) {
  const rows = String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [label, amount] = line.split(/[:：,，\s]+/);
      return {
        id: `${index}-${label || "allowance"}`,
        label: label || "",
        amount: numberValue(amount || 0),
      };
    });
  return rows.length ? rows : [{ id: "allowance-empty", label: "", amount: "" }];
}

function buildAllowanceText(rows) {
  return rows
    .filter((row) => row.label || numberValue(row.amount) !== "")
    .map((row) => `${row.label || ""} ${numberValue(row.amount) || 0}`.trim())
    .join("\n");
}

function employeeTypeLabel(type, t) {
  return {
    driver: t.driver,
    dispatcher: t.dispatcher,
    internal: t.internal,
    admin: t.admin,
    staff: t.staff,
  }[type] || t.staff;
}

function allowanceTotal(employee) {
  return (employee.allowances || []).reduce((sum, item) => sum + Number(item.amount || 0), 0);
}

function validatePayrollForm(form, t) {
  const errors = [];
  if (!String(form.name || "").trim()) errors.push(`${t.name}${t.required}`);
  if (numberValue(form.base_salary) === "" || Number(numberValue(form.base_salary)) < 0) errors.push(t.baseSalary);
  if (!Number.isInteger(Number(form.dependent_count)) || Number(form.dependent_count) < 0) errors.push(t.dependents);
  if (!String(form.social_insurance_prefecture || "").trim()) errors.push(t.prefecture);
  parseAllowanceRows(form.allowances_text).forEach((row) => {
    if (row.label && (numberValue(row.amount) === "" || Number(numberValue(row.amount)) < 0)) {
      errors.push(`${t.allowanceName}: ${row.label}`);
    }
  });
  return errors;
}

function amountForLabels(items, labels) {
  return (items || [])
    .filter((item) => labels.includes(item.label))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
}

function otherAmount(items, knownRows) {
  const knownLabels = new Set(knownRows.flatMap((row) => row.labels));
  return (items || [])
    .filter((item) => !knownLabels.has(item.label))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
}

function buildPayslipRows(payslip, rows, otherKey, knownRows, t) {
  const baseRows = rows
    .map((row) => ({
      label: t[row.key],
      amount: amountForLabels(payslip?.[otherKey === "otherAllowance" ? "earnings" : "deductions"], row.labels),
      hideWhenZero: row.hideWhenZero,
    }))
    .filter((row) => !(row.hideWhenZero && row.amount === 0));
  const source = otherKey === "otherAllowance" ? payslip?.earnings : payslip?.deductions;
  return [
    ...baseRows,
    {
      label: t[otherKey],
      amount: otherAmount(source, knownRows),
    },
  ];
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "") ?? "-";
}

const ANNUAL_TAXABLE_EARNING_LABELS = [
  "基本給",
  "時間外手当",
  "残業手当",
  "役職手当",
  "資格手当",
  "住居手当",
  "家族手当",
];

const ANNUAL_NON_TAXABLE_EARNING_LABELS = [
  "通勤手当",
  "非課税通勤費",
  "立替交通費",
];

const ANNUAL_DEDUCTION_LABELS = [
  "健康保険",
  "介護保険",
  "厚生年金",
  "雇用保険",
  "源泉所得税",
  "所得税",
  "住民税",
  "財形貯蓄",
  "生命保険料",
  "積立金",
  "組合費",
  "その他控除",
];

function uniqueLabels(preferred, discovered) {
  return [...new Set([...preferred, ...discovered.filter(Boolean)])];
}

function itemAmount(payslip, sourceKey, label) {
  return (payslip?.[sourceKey] || [])
    .filter((item) => item.label === label)
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
}

function buildAnnualLedgerRows(employeeRecord, t) {
  const payslips = Object.values(employeeRecord?.months || {});
  const taxableLabels = uniqueLabels(
    ANNUAL_TAXABLE_EARNING_LABELS,
    payslips.flatMap((payslip) => (
      payslip.earnings || []
    ).filter((item) => item.taxable !== false && !ANNUAL_NON_TAXABLE_EARNING_LABELS.includes(item.label)).map((item) => item.label)),
  );
  const nonTaxableLabels = uniqueLabels(
    ANNUAL_NON_TAXABLE_EARNING_LABELS,
    payslips.flatMap((payslip) => (payslip.earnings || []).filter((item) => item.taxable === false).map((item) => item.label)),
  );
  const deductionLabels = uniqueLabels(
    ANNUAL_DEDUCTION_LABELS,
    payslips.flatMap((payslip) => (payslip.deductions || []).map((item) => item.label)),
  );

  return [
    { kind: "section", label: t.taxablePayGroup, tone: "green" },
    ...taxableLabels.map((label) => ({ label, value: (payslip) => itemAmount(payslip, "earnings", label) })),
    { kind: "total", label: t.taxablePayTotal, tone: "green", value: (payslip) => Number(payslip?.taxable_pay || 0) },
    { kind: "section", label: t.nonTaxablePayGroup, tone: "yellow" },
    ...nonTaxableLabels.map((label) => ({ label, value: (payslip) => itemAmount(payslip, "earnings", label) })),
    { kind: "total", label: t.nonTaxablePayTotal, tone: "yellow", value: (payslip) => Number(payslip?.non_taxable_pay || 0) },
    { kind: "total", label: t.grossPay, tone: "plain", value: (payslip) => Number(payslip?.gross_pay || 0) },
    { kind: "section", label: t.deductionsSection, tone: "blue" },
    ...deductionLabels.map((label) => ({ label, value: (payslip) => itemAmount(payslip, "deductions", label) })),
    { kind: "total", label: t.totalDeductions, tone: "blue", value: (payslip) => Number(payslip?.total_deductions || 0) },
    { kind: "section", label: t.attendanceGroup, tone: "gray" },
    { label: t.workDays, isAttendance: true, value: (payslip) => Number(payslip?.attendance?.work_days || 0) },
    { label: t.attendanceDays, isAttendance: true, value: (payslip) => Number(payslip?.attendance?.attendance_days || 0) },
    { label: t.absenceDays, isAttendance: true, value: (payslip) => Number(payslip?.attendance?.absence_days || 0) },
    { label: t.workHours, isAttendance: true, value: (payslip) => Number(payslip?.attendance?.work_hours || 0) },
    { label: t.overtimeHours, isAttendance: true, value: (payslip) => Number(payslip?.attendance?.overtime_hours || 0) },
    { kind: "net", label: t.netPay, tone: "orange", value: (payslip) => Number(payslip?.net_pay || 0) },
  ];
}

export default function AdminPayroll({ payrollProps, language = "zh", onLanguageChange }) {
  const {
    annualPayroll,
    closePayrollEmployeeForm,
    closePayslipEditor,
    companySettingsForm,
    driverImportOptions,
    editingPayrollEmployee,
    editingPayslip,
    fetchAnnualPayroll,
    handleDownloadPayslipPdf,
    handleExportPayrollExcel,
    handleExportPayrollPdfs,
    handleGeneratePayrollRun,
    handleLockPayrollRun,
    handleReopenPayslip,
    handleSavePayrollEmployee,
    handleSavePayslipAdjustment,
    openCreatePayrollEmployee,
    openEditPayslip,
    openEditPayrollEmployee,
    payslipEditorForm,
    payrollEmployeeForm,
    payrollEmployees = [],
    payrollLoading,
    payrollMonth,
    payrollYear,
    setPayrollEmployeeForm,
    setPayslipEditorForm,
    setPayrollMonth,
    setPayrollYear,
    showPayrollEmployeeForm,
  } = payrollProps || {};
  const [localLanguage, setLocalLanguage] = useState(language);
  const [payrollMode, setPayrollMode] = useState("payslips");
  const [historyOpenRequest, setHistoryOpenRequest] = useState("");
  const activeLanguage = language || localLanguage;
  const t = LANG[activeLanguage] || LANG.zh;
  const annualPayslips = (annualPayroll?.employees || []).flatMap((row) => Object.values(row.months || {}));
  const activeModeTitle = payrollMode === "profiles" ? t.profileTitle : t.payslipTitle;
  const activeModeSubtitle = payrollMode === "profiles" ? t.profileSubtitle : t.payslipSubtitle;

  const handleLanguageChange = (nextLanguage) => {
    setLocalLanguage(nextLanguage);
    if (onLanguageChange) onLanguageChange(nextLanguage);
  };

  const handleOpenHistoricalMonth = async () => {
    const targetMonth = String(payrollMonth || "");
    if (!/^\d{4}-\d{2}$/.test(targetMonth)) return;
    const targetYear = targetMonth.slice(0, 4);
    setPayrollYear(targetYear);
    const annualData = await fetchAnnualPayroll(targetYear);
    if (annualData) setHistoryOpenRequest(`${targetMonth}:${Date.now()}`);
  };

  return (
    <section className="internal-luxury-card p-4 sm:p-5">
      {payrollProps?.attendanceRequest && <details className="mb-5 border border-amber-300/30 rounded-xl p-3"><summary className="cursor-pointer font-bold">{activeLanguage === 'ja' ? '運転手の勤怠・立替金' : '司机出勤与立替金'}</summary><AttendanceWorkspace admin request={payrollProps.attendanceRequest} language={activeLanguage}/></details>}
      <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <span className="premium-chip inline-flex items-center gap-2 border-amber-300/30 text-[#f3d98b]">
            <WalletCards className="h-3.5 w-3.5" />
            <span>{t.badge}</span>
          </span>
          <h3 className="mt-2 text-xl font-semibold text-white">{activeModeTitle}</h3>
          <p className="mt-1 max-w-3xl text-sm text-slate-400">{activeModeSubtitle}</p>
        </div>
        <div className="flex flex-col gap-3">
          <div className="grid rounded-lg border border-amber-300/20 bg-black/30 p-1 sm:grid-cols-2" data-testid="payroll-mode-tabs">
            <button
              type="button"
              id="payslip-confirmation-mode"
              onClick={() => setPayrollMode("payslips")}
              className={`rounded-md px-4 py-2 text-sm font-semibold transition ${payrollMode === "payslips" ? "bg-amber-300/15 text-[#f3d98b]" : "text-slate-400 hover:text-white"}`}
            >
              <span className="inline-flex items-center gap-2"><Table2 className="h-4 w-4" />{t.payslipMode}</span>
            </button>
            <button
              type="button"
              id="employee-profile-mode"
              onClick={() => setPayrollMode("profiles")}
              className={`rounded-md px-4 py-2 text-sm font-semibold transition ${payrollMode === "profiles" ? "bg-amber-300/15 text-[#f3d98b]" : "text-slate-400 hover:text-white"}`}
            >
              {t.profileMode}
            </button>
          </div>
          {payrollMode === "profiles" ? (
            <button type="button" onClick={openCreatePayrollEmployee} className="internal-gold-button justify-center">
              <Plus className="h-4 w-4" />
              {t.add}
            </button>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-[120px_170px_auto]">
                <label className="flex items-center gap-2 rounded-lg border border-amber-300/20 bg-black/25 px-3 py-2">
                  <CalendarRange className="h-4 w-4 text-[#f3d98b]" />
                  <input
                    type="number"
                  min="2020"
                    max="2100"
                    value={payrollYear}
                    onChange={(event) => {
                      const nextYear = event.target.value;
                      setPayrollYear(nextYear);
                    }}
                    onBlur={() => /^\d{4}$/.test(payrollYear) && fetchAnnualPayroll(payrollYear)}
                    className="min-w-0 flex-1 border-0 bg-transparent text-sm font-semibold text-white outline-none"
                    aria-label={t.yearLabel}
                  />
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-amber-300/20 bg-black/25 px-3 py-2">
                  <span className="text-xs font-semibold text-slate-400">{t.historyMonth}</span>
                  <input
                    type="month"
                    value={payrollMonth}
                    onChange={(event) => setPayrollMonth(event.target.value)}
                    className="min-w-0 flex-1 border-0 bg-transparent text-sm font-semibold text-white outline-none"
                    aria-label={t.historyMonth}
                  />
                </label>
                <button type="button" onClick={handleOpenHistoricalMonth} disabled={payrollLoading || !/^\d{4}-\d{2}$/.test(payrollMonth || "")} className="internal-graphite-button !rounded-lg">
                  <SquarePen className="h-4 w-4" />
                  {t.openHistoryMonth}
                </button>
              </div>
              <p className="max-w-xl text-xs leading-5 text-slate-500">{t.historyMonthHint}</p>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <PayrollStat label={t.statsEmployees} value={payrollEmployees.length} />
        <PayrollStat label={t.statsRuns} value={(annualPayroll?.runs || []).length} />
        <PayrollStat label={t.statsPayslips} value={annualPayslips.length} />
        <PayrollStat label={t.statsNet} value={formatCurrency(annualPayslips.reduce((sum, item) => sum + Number(item.net_pay || 0), 0))} />
      </div>

      <div className="mt-5">
        {payrollMode === "profiles" ? (
          <EmployeeList employees={payrollEmployees} onEdit={openEditPayrollEmployee} t={t} />
        ) : (
          <PayrollLedgerPanel
            annualPayroll={annualPayroll}
            company={companySettingsForm || annualPayroll?.company || {}}
            editingPayslip={editingPayslip}
            editorForm={payslipEditorForm}
            loading={payrollLoading}
            month={payrollMonth}
            openMonthRequest={historyOpenRequest}
            onChangeEditor={setPayslipEditorForm}
            onCloseEditor={closePayslipEditor}
            onDownloadPdf={handleDownloadPayslipPdf}
            onExportExcel={handleExportPayrollExcel}
            onExportPdfs={handleExportPayrollPdfs}
            onGenerateMonth={handleGeneratePayrollRun}
            onLockRun={handleLockPayrollRun}
            onOpenPayslip={openEditPayslip}
            onReopenPayslip={handleReopenPayslip}
            onSavePayslip={handleSavePayslipAdjustment}
            onSelectMonth={setPayrollMonth}
            t={t}
          />
        )}
      </div>

      {showPayrollEmployeeForm && createPortal(
        <PayrollEmployeeForm
          editingEmployee={editingPayrollEmployee}
          form={payrollEmployeeForm}
          language={activeLanguage}
          onChange={setPayrollEmployeeForm}
          onClose={closePayrollEmployeeForm}
          onLanguageChange={handleLanguageChange}
          onSave={handleSavePayrollEmployee}
          driverImportOptions={driverImportOptions || []}
        />,
        document.body,
      )}
    </section>
  );
}

function PayrollLedgerPanel({
  annualPayroll,
  company,
  editingPayslip,
  editorForm,
  loading,
  month,
  openMonthRequest,
  onChangeEditor,
  onCloseEditor,
  onDownloadPdf,
  onExportExcel,
  onExportPdfs,
  onGenerateMonth,
  onLockRun,
  onOpenPayslip,
  onReopenPayslip,
  onSavePayslip,
  onSelectMonth,
  t,
}) {
  const employees = useMemo(() => annualPayroll?.employees || [], [annualPayroll?.employees]);
  const year = annualPayroll?.year || String(month || "").slice(0, 4);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedMonthKey, setSelectedMonthKey] = useState(String(month || "").slice(-2) || "01");
  const [ledgerView, setLedgerView] = useState("annual");

  useEffect(() => {
    if (!employees.length) {
      setSelectedEmployeeId("");
      return;
    }
    if (!employees.some((row) => row.employee?.id === selectedEmployeeId)) {
      setSelectedEmployeeId(employees[0].employee?.id || "");
    }
  }, [employees, selectedEmployeeId]);

  const employeeRecord = employees.find((row) => row.employee?.id === selectedEmployeeId) || employees[0];
  const selectedMonth = `${year}-${selectedMonthKey}`;
  const selectedPayslip = employeeRecord?.months?.[selectedMonthKey] || null;
  const selectedRun = (annualPayroll?.runs || []).find((run) => run.month === selectedMonth) || null;

  useEffect(() => {
    if (ledgerView === "monthly" && selectedPayslip && editingPayslip?.id !== selectedPayslip.id) {
      onOpenPayslip(selectedPayslip);
    }
    // The payslip id is the stable handoff between annual refreshes and the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerView, selectedPayslip?.id, editingPayslip?.id]);

  useEffect(() => {
    const requestedMonth = String(openMonthRequest || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(requestedMonth) || requestedMonth.slice(0, 4) !== year) return;
    const requestedMonthKey = requestedMonth.slice(-2);
    setSelectedMonthKey(requestedMonthKey);
    setLedgerView("monthly");
    onSelectMonth(requestedMonth);
    const payslip = employeeRecord?.months?.[requestedMonthKey];
    if (payslip) onOpenPayslip(payslip);
    else onCloseEditor();
    // The request value includes a timestamp so the same month can be opened repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMonthRequest]);

  const openMonth = (monthKey) => {
    const nextMonth = `${year}-${monthKey}`;
    setSelectedMonthKey(monthKey);
    setLedgerView("monthly");
    onSelectMonth(nextMonth);
    const payslip = employeeRecord?.months?.[monthKey];
    if (payslip) onOpenPayslip(payslip);
    else onCloseEditor();
  };

  const selectEmployee = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    setLedgerView("annual");
    onCloseEditor();
  };

  return (
    <div className="internal-luxury-panel overflow-hidden">
      <div className="grid gap-3 border-b border-amber-300/20 bg-black/20 p-4 lg:grid-cols-[minmax(240px,1fr)_auto_auto] lg:items-end">
        <label>
          <span className="mb-1 block text-xs font-semibold text-slate-400">{t.selectEmployee}</span>
          <select
            value={employeeRecord?.employee?.id || ""}
            onChange={(event) => selectEmployee(event.target.value)}
            className="w-full rounded-lg border border-amber-300/20 bg-black/35 px-3 py-2.5 text-sm font-semibold text-white outline-none"
          >
            {employees.map((row) => (
              <option key={row.employee?.id} value={row.employee?.id}>
                {row.employee?.name || "-"} {row.employee?.employee_code ? `/ ${row.employee.employee_code}` : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 rounded-lg border border-white/10 bg-black/30 p-1">
          <button
            type="button"
            onClick={() => {
              setLedgerView("annual");
              onCloseEditor();
            }}
            className={`rounded-md px-4 py-2 text-sm font-semibold ${ledgerView === "annual" ? "bg-white/10 text-white" : "text-slate-400"}`}
          >
            {t.annualView}
          </button>
          <button
            type="button"
            onClick={() => openMonth(selectedMonthKey)}
            className={`rounded-md px-4 py-2 text-sm font-semibold ${ledgerView === "monthly" ? "bg-white/10 text-white" : "text-slate-400"}`}
          >
            {t.monthlyView}
          </button>
        </div>
        <div className="text-right text-xs leading-5 text-slate-400">
          <div className="font-semibold text-white">{company.company_name || "湘南国際株式会社"}</div>
          <div>{t.officialCompanyFixed}</div>
        </div>
      </div>
      {!employeeRecord ? (
        <div className="px-4 py-16 text-center text-sm text-slate-400">{t.emptyEmployees}</div>
      ) : ledgerView === "annual" ? (
        <AnnualPayrollTable employeeRecord={employeeRecord} onOpenMonth={openMonth} t={t} year={year} />
      ) : (
        <MonthlyPayrollEditor
          company={company}
          editingPayslip={editingPayslip || selectedPayslip}
          form={editorForm}
          loading={loading}
          month={selectedMonth}
          onBack={() => {
            setLedgerView("annual");
            onCloseEditor();
          }}
          onChange={onChangeEditor}
          onDownloadPdf={onDownloadPdf}
          onExportExcel={() => onExportExcel(selectedRun?.id)}
          onExportPdfs={() => onExportPdfs(selectedRun?.id)}
          onGenerate={() => onGenerateMonth(selectedMonth)}
          onLock={() => onLockRun(selectedRun?.id)}
          onReopen={() => onReopenPayslip((editingPayslip || selectedPayslip)?.id)}
          onSave={onSavePayslip}
          run={selectedRun}
          t={t}
        />
      )}
    </div>
  );
}

function AnnualPayrollTable({ employeeRecord, onOpenMonth, t, year }) {
  const rows = useMemo(() => buildAnnualLedgerRows(employeeRecord, t), [employeeRecord, t]);
  const months = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
  const employee = employeeRecord.employee || {};

  return (
    <div className="bg-[#f7f8f6] text-[#1d2328]">
      <div className="flex flex-col gap-2 border-b border-[#b7c0c6] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-xl font-bold tracking-normal">{year}年 給与一覧表</h4>
          <p className="mt-1 text-xs text-[#5f6970]">社員No {employee.employee_code || "-"} / 氏名 {employee.name || "-"}</p>
        </div>
        <p className="text-xs text-[#5f6970]">月份をクリックすると月次明細を直接編集できます</p>
      </div>
      <div className="max-h-[68vh] overflow-auto">
        <table className="w-full min-w-[1680px] border-collapse text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 w-44 border border-[#87939b] bg-[#e8ece8] px-3 py-2 text-left font-bold">項目</th>
              {months.map((monthKey) => (
                <th key={monthKey} className="w-28 border border-[#87939b] bg-[#d9e9cf] px-2 py-2 text-center font-bold">
                  <button type="button" onClick={() => onOpenMonth(monthKey)} className="w-full text-[#24301f] hover:underline">
                    {Number(monthKey)}月
                  </button>
                </th>
              ))}
              <th className="sticky right-0 z-30 w-32 border border-[#87939b] bg-[#d8c78c] px-3 py-2 text-right font-bold">{t.annualTotal}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const toneClass = {
                green: "bg-[#dcebd2]",
                yellow: "bg-[#fff0bd]",
                blue: "bg-[#d7e8f4]",
                gray: "bg-[#e6e8e8]",
                orange: "bg-[#f2b36b]",
                plain: "bg-[#f4f5f3]",
              }[row.tone] || "bg-white";
              if (row.kind === "section") {
                return (
                  <tr key={`${row.kind}-${row.label}-${rowIndex}`}>
                    <th className={`sticky left-0 z-10 border border-[#87939b] px-3 py-2 text-left font-bold ${toneClass}`}>{row.label}</th>
                    <td colSpan="13" className={`border border-[#87939b] ${toneClass}`} />
                  </tr>
                );
              }
              const monthValues = months.map((monthKey) => row.value(employeeRecord.months?.[monthKey]));
              const annualTotal = monthValues.reduce((sum, value) => sum + Number(value || 0), 0);
              return (
                <tr key={`${row.label}-${rowIndex}`} className={row.kind ? "font-bold" : ""}>
                  <th className={`sticky left-0 z-10 border border-[#87939b] px-3 py-1.5 text-left ${row.kind ? toneClass : "bg-white"}`}>{row.label}</th>
                  {months.map((monthKey, index) => (
                    <td key={monthKey} className={`border border-[#87939b] p-0 text-right tabular-nums ${row.kind ? toneClass : "bg-white"}`}>
                      <button type="button" onClick={() => onOpenMonth(monthKey)} className="h-full min-h-8 w-full px-2 py-1.5 text-right hover:bg-[#f6ecd0]">
                        {row.isAttendance ? (monthValues[index] || "-") : (monthValues[index] ? formatCurrency(monthValues[index]) : "-")}
                      </button>
                    </td>
                  ))}
                  <td className={`sticky right-0 z-10 border border-[#87939b] px-3 py-1.5 text-right font-bold tabular-nums ${toneClass}`}>
                    {row.isAttendance ? annualTotal.toLocaleString() : formatCurrency(annualTotal)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthlyPayrollEditor({
  company,
  editingPayslip,
  form,
  loading,
  month,
  onBack,
  onChange,
  onDownloadPdf,
  onExportExcel,
  onExportPdfs,
  onGenerate,
  onLock,
  onReopen,
  onSave,
  run,
  t,
}) {
  const locked = editingPayslip?.status === "locked";
  if (!editingPayslip || !form) {
    return (
      <div className="bg-[#f7f8f6] px-5 py-14 text-center text-[#1d2328]">
        <FileText className="mx-auto h-8 w-8 text-[#8a6b28]" />
        <h4 className="mt-3 text-lg font-bold">{month}</h4>
        <p className="mt-2 text-sm text-[#69737a]">{t.noMonthlyPayslip}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button type="button" onClick={onBack} className="internal-graphite-button !rounded-lg">{t.backToAnnual}</button>
          <button type="button" onClick={onGenerate} disabled={loading} className="internal-gold-button !rounded-lg">
            <FileText className="h-4 w-4" />
            {t.generateMonth}
          </button>
        </div>
      </div>
    );
  }

  const updateBasic = (key, value) => onChange({ ...form, basic_info: { ...form.basic_info, [key]: value } });
  const updateAttendance = (key, value) => onChange({ ...form, attendance: { ...form.attendance, [key]: value } });
  const updateItem = (section, index, key, value) => {
    const rows = (form[section] || []).map((item, idx) => idx === index ? { ...item, [key]: value } : item);
    onChange({ ...form, [section]: rows });
  };
  const addItem = (section, taxable = true) => onChange({
    ...form,
    [section]: [...(form[section] || []), { label: "", amount: "", taxable, source: "manual_editor" }],
  });
  const removeItem = (section, index) => onChange({
    ...form,
    [section]: (form[section] || []).filter((_, idx) => idx !== index),
  });
  const grossPay = (form.earnings || []).reduce((sum, item) => sum + Number(numberValue(item.amount) || 0), 0);
  const totalDeductions = (form.deductions || []).reduce((sum, item) => sum + Number(numberValue(item.amount) || 0), 0);
  const netPay = grossPay - totalDeductions;

  return (
    <div className="bg-[#f7f3e8] p-4 text-[#17130b] sm:p-5">
      <div className="flex flex-col gap-4 border-b border-[#b49245]/40 pb-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex min-w-0 gap-4">
          {company.seal_data_url && <img src={company.seal_data_url} alt="会社印" className="h-16 w-16 shrink-0 object-contain" />}
          <div className="min-w-0">
            <div className="text-sm font-bold">{company.company_name || "湘南国際株式会社"}</div>
            <div className="mt-1 text-xs leading-5 text-[#6f5d38]">{company.company_address}</div>
            <div className="text-xs text-[#6f5d38]">TEL {company.company_phone}</div>
            <h4 className="mt-2 text-2xl font-bold tracking-normal">{month} 給与明細書</h4>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onBack} className="payroll-sheet-button">{t.backToAnnual}</button>
          <button type="button" onClick={() => onDownloadPdf(editingPayslip)} className="payroll-sheet-button"><Download className="h-4 w-4" />{t.pdfOutput}</button>
          <button type="button" onClick={onExportPdfs} disabled={loading || !run?.id} className="payroll-sheet-button"><Download className="h-4 w-4" />{t.batchPdfOutput}</button>
          <button type="button" onClick={onExportExcel} className="payroll-sheet-button"><Table2 className="h-4 w-4" />{t.excelOutput}</button>
          {locked ? (
            <button type="button" onClick={() => onReopen(editingPayslip.id)} disabled={loading} className="payroll-sheet-save"><SquarePen className="h-4 w-4" />{t.revisePayslip}</button>
          ) : (
            <>
              <button type="button" onClick={() => onLock(run?.id || editingPayslip.run_id)} disabled={loading} className="payroll-sheet-button"><LockKeyhole className="h-4 w-4" />{t.confirmPayslip}</button>
              <button type="button" onClick={onSave} disabled={loading} className="payroll-sheet-save"><Save className="h-4 w-4" />{t.savePayslip}</button>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <PayrollInput label={t.employeeName} value={form.basic_info.employee_name} onChange={(value) => updateBasic("employee_name", value)} disabled={locked} light />
        <PayrollInput label={t.employeeNo} value={form.basic_info.employee_code} onChange={(value) => updateBasic("employee_code", value)} disabled={locked} light />
        <PayrollInput label={t.department} value={form.basic_info.department} onChange={(value) => updateBasic("department", value)} disabled={locked} light />
        <PayrollInput label={t.paymentDate} type="date" value={form.basic_info.payment_date} onChange={(value) => updateBasic("payment_date", value)} hint={locked ? "" : t.paymentDateHint} disabled={locked} light />
        <label>
          <span className="mb-1 block text-xs font-bold text-[#6f5d38]">{t.taxCategory}</span>
          <select value={form.basic_info.tax_category || "kou"} onChange={(event) => updateBasic("tax_category", event.target.value)} disabled={locked} className="payroll-table-field">
            <option value="kou">{t.taxKou}</option>
            <option value="otsu">{t.taxOtsu}</option>
          </select>
          <span className="mt-1 block text-[11px] leading-4 text-[#7b6c4b]">{t.taxCategoryHint}</span>
        </label>
        <div className="flex items-end">
          <span className={`inline-flex rounded-md px-3 py-2 text-xs font-bold ${locked ? "bg-[#d5dde2] text-[#42515b]" : "bg-[#fff0bd] text-[#6a541c]"}`}>
            {locked ? t.locked : editingPayslip?.correction_pending ? t.correcting : t.draft}
          </span>
        </div>
      </div>

      <section className="mt-4 border border-[#b7c0c6] bg-white/75">
        <h5 className="border-b border-[#b7c0c6] bg-[#e6e8e8] px-3 py-2 text-sm font-bold">{t.attendanceGroup}</h5>
        <div className="grid gap-px bg-[#b7c0c6] sm:grid-cols-5">
          {[
            ["work_days", t.workDays],
            ["attendance_days", t.attendanceDays],
            ["absence_days", t.absenceDays],
            ["work_hours", t.workHours],
            ["overtime_hours", t.overtimeHours],
          ].map(([key, label]) => (
            <label key={key} className="bg-white p-3">
              <span className="mb-1 block text-xs font-bold text-[#5f6970]">{label}</span>
              <input type="number" value={form.attendance[key] ?? ""} onChange={(event) => updateAttendance(key, event.target.value)} disabled={locked} className="payroll-table-field text-right" />
            </label>
          ))}
        </div>
      </section>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <EditablePayslipItems
          title={t.earningsSection}
          rows={form.earnings || []}
          section="earnings"
          onAdd={() => addItem("earnings", true)}
          onRemove={removeItem}
          onUpdate={updateItem}
          t={t}
          taxable
          disabled={locked}
          light
        />
        <EditablePayslipItems
          title={t.deductionsSection}
          rows={form.deductions || []}
          section="deductions"
          onAdd={() => addItem("deductions", false)}
          onRemove={removeItem}
          onUpdate={updateItem}
          t={t}
          disabled={locked}
          light
        />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <PayslipTotal label={t.grossPay} value={grossPay} />
        <PayslipTotal label={t.totalDeductions} value={totalDeductions} danger />
        <PayslipTotal label={t.netPay} value={netPay} highlight />
      </div>
      <label className="mt-4 block">
        <span className="mb-1 block text-xs font-bold text-[#6f5d38]">{t.notes}</span>
        <textarea value={form.notes || ""} onChange={(event) => onChange({ ...form, notes: event.target.value })} disabled={locked} className="payroll-table-field min-h-20" />
      </label>
    </div>
  );
}

function PayrollStat({ label, value }) {
  return (
    <div className="internal-luxury-panel px-4 py-3">
      <div className="text-xs font-medium text-slate-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function EmployeeList({ employees, onEdit, t }) {
  return (
    <div className="internal-luxury-panel overflow-hidden">
      <div className="border-b border-amber-300/20 px-4 py-3">
        <h4 className="text-sm font-semibold text-white">{t.employeeList}</h4>
        <p className="mt-1 text-xs text-slate-500">{t.profileSubtitle}</p>
      </div>
      <div className="overflow-auto">
        <table className="w-full min-w-[900px] text-xs">
          <thead className="bg-black/30">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-400">{t.employee}</th>
              <th className="px-4 py-3 text-left font-medium text-slate-400">{t.type}</th>
              <th className="px-4 py-3 text-right font-medium text-slate-400">{t.baseSalary}</th>
              <th className="px-4 py-3 text-right font-medium text-slate-400">{t.standardMonthlyShort}</th>
              <th className="px-4 py-3 text-right font-medium text-slate-400">{t.allowance}</th>
              <th className="px-4 py-3 text-left font-medium text-slate-400">{t.status}</th>
              <th className="px-4 py-3 text-right font-medium text-slate-400">{t.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {employees.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-10 text-center text-slate-400">{t.emptyEmployees}</td>
              </tr>
            ) : employees.map((employee) => (
              <tr key={employee.id} className="hover:bg-white/[0.035]">
                <td className="px-4 py-4">
                  <div className="text-sm font-semibold text-white">{employee.name}</div>
                  <div className="mt-1 text-[11px] text-slate-500">{employee.phone || "-"}</div>
                </td>
                <td className="px-4 py-4 text-slate-300">{employeeTypeLabel(employee.employee_type, t)}</td>
                <td className="px-4 py-4 text-right font-semibold text-slate-100">{formatCurrency(employee.base_salary)}</td>
                <td className="px-4 py-4 text-right text-slate-300">{formatCurrency(employee.standard_monthly_remuneration || 0)}</td>
                <td className="px-4 py-4 text-right text-[#f3d98b]">{formatCurrency(allowanceTotal(employee))}</td>
                <td className="px-4 py-4">
                  <span className={`internal-status-badge ${employee.is_active === false ? "bg-slate-500/15 text-slate-400" : "bg-emerald-500/15 text-emerald-300"}`}>
                    {employee.is_active === false ? t.inactive : t.active}
                  </span>
                </td>
                <td className="px-4 py-4 text-right">
                  <button type="button" onClick={() => onEdit(employee)} className="internal-graphite-button !px-3 !py-1.5 !text-xs">
                    <SquarePen className="h-3.5 w-3.5" />
                    {t.edit}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayrollRunPanel({ loading, runs, selectedRun, onSelectRun, onLockRun, onDownloadPdf, onEditPayslip, t }) {
  const payslips = selectedRun?.payslips || [];
  const [selectedPayslipId, setSelectedPayslipId] = useState("");
  const selectedPayslip = payslips.find((payslip) => payslip.id === selectedPayslipId) || payslips[0];

  return (
    <div className="internal-luxury-panel overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-amber-300/20 px-4 py-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-white">{t.batch}</h4>
          <p className="mt-1 text-xs text-slate-400">{t.batchHint}</p>
          <p className="mt-1 text-xs text-slate-500">{t.residentTaxPolicy}</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-4">
          <select value={selectedRun?.id || ""} onChange={(event) => onSelectRun(event.target.value)} className="rounded-xl border border-amber-300/20 bg-black/30 px-3 py-2 text-sm text-white">
            <option value="">{t.selectBatch}</option>
            {runs.map((run) => (
              <option key={run.id} value={run.id}>
                {run.month} / {run.status === "locked" ? t.locked : t.draft} / {run.payslip_count || 0}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => onDownloadPdf(selectedPayslip)} disabled={loading || !selectedPayslip} className="internal-graphite-button !py-2">
            <Download className="h-4 w-4" />
            {t.pdfOutput}
          </button>
          <button type="button" onClick={() => onEditPayslip(selectedPayslip)} disabled={loading || !selectedPayslip || selectedRun?.status === "locked"} className="internal-graphite-button !py-2">
            <SquarePen className="h-4 w-4" />
            {t.editPayslip}
          </button>
          <button type="button" onClick={() => onLockRun(selectedRun?.id)} disabled={loading || !selectedRun || selectedRun.status === "locked"} className="internal-gold-button !py-2">
            <LockKeyhole className="h-4 w-4" />
            {t.confirmPayslip}
          </button>
        </div>
      </div>
      {payslips.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-slate-400">{t.noPayslips}</div>
      ) : (
        <div className="grid gap-4 p-4 xl:grid-cols-[280px_1fr]">
          <div className="rounded-2xl border border-amber-300/15 bg-black/25 p-3">
            <div className="mb-2 text-xs font-semibold text-slate-400">{t.employeeName}</div>
            <div className="grid gap-2">
              {payslips.map((payslip) => (
                <button
                  key={payslip.id}
                  type="button"
                  onClick={() => setSelectedPayslipId(payslip.id)}
                  className={`rounded-xl border px-3 py-2 text-left transition ${selectedPayslip?.id === payslip.id ? "border-amber-300/45 bg-amber-300/10 text-[#f3d98b]" : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-amber-300/25"}`}
                >
                  <span className="block text-sm font-semibold">{payslip.employee_name}</span>
                  <span className="mt-1 block text-xs text-slate-500">{formatCurrency(payslip.net_pay)}</span>
                </button>
              ))}
            </div>
          </div>
          <FormalPayslipSheet payslip={selectedPayslip} run={selectedRun} t={t} />
        </div>
      )}
    </div>
  );
}

function FormalPayslipSheet({ payslip, run, t }) {
  const earningRows = buildPayslipRows(payslip, EARNING_ROWS, "otherAllowance", EARNING_ROWS, t);
  const deductionRows = buildPayslipRows(payslip, DEDUCTION_ROWS, "otherDeduction", DEDUCTION_ROWS, t);
  const basic = payslip?.basic_info || {};
  const basicInfo = [
    [t.paymentMonth, pickFirst(basic.month, payslip?.month, run?.month)],
    [t.paymentDate, pickFirst(basic.payment_date)],
    [t.employeeName, pickFirst(basic.employee_name, payslip?.employee_name)],
    [t.employeeNo, pickFirst(basic.employee_code, payslip?.employee_code, payslip?.employee_id)],
    [t.department, pickFirst(basic.department, payslip?.employee_type)],
    [t.workDays, pickFirst(payslip?.work_days, payslip?.attendance?.work_days)],
    [t.attendanceDays, pickFirst(payslip?.attendance_days, payslip?.attendance?.attendance_days)],
    [t.absenceDays, pickFirst(payslip?.absence_days, payslip?.attendance?.absence_days)],
    [t.workHours, pickFirst(payslip?.work_hours, payslip?.attendance?.work_hours)],
    [t.overtimeHours, pickFirst(payslip?.overtime_hours, payslip?.attendance?.overtime_hours)],
  ];

  return (
    <div className="formal-payslip-sheet rounded-2xl border border-amber-300/30 bg-[#f7f3e8] p-5 text-[#17130b] shadow-[0_24px_80px_rgba(0,0,0,0.28)]">
      <div className="flex flex-col gap-3 border-b border-[#b49245]/35 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8a6b28]">{pickFirst(basic.company_name, "Shonan International")}</div>
          <h3 className="mt-1 text-2xl font-bold tracking-normal text-[#15110a]">給与明細</h3>
          <div className="mt-1 text-xs text-[#6f5d38]">{[basic.company_address, basic.company_phone].filter(Boolean).join(" / ")}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-sm font-semibold text-[#8a6b28]">{pickFirst(payslip?.month, run?.month)}</div>
        </div>
      </div>

      <section className="mt-4">
        <h4 className="mb-2 text-sm font-bold text-[#8a6b28]">{t.payslipBasicInfo}</h4>
        <div className="grid overflow-hidden rounded-xl border border-[#d8c38a] sm:grid-cols-2 lg:grid-cols-5">
          {basicInfo.map(([label, value]) => (
            <div key={label} className="border-b border-r border-[#e4d6ad] bg-white/55 px-3 py-2 last:border-r-0">
              <div className="text-[11px] font-semibold text-[#8a6b28]">{label}</div>
              <div className="mt-1 min-h-5 text-sm font-semibold text-[#17130b]">{value}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <PayslipTable title={t.earningsSection} rows={earningRows} tone="gold" />
        <PayslipTable title={t.deductionsSection} rows={deductionRows} tone="red" />
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <PayslipTotal label={t.grossPay} value={payslip?.gross_pay} />
        <PayslipTotal label={t.totalDeductions} value={payslip?.total_deductions} danger />
        <PayslipTotal label="税後給与" value={payslip?.wage_net_pay ?? payslip?.net_pay} />
        <PayslipTotal label="立替金" value={payslip?.advance_reimbursement_total || 0} />
        <PayslipTotal label={t.netPay} value={payslip?.net_pay} highlight />
      </div>
    </div>
  );
}

function PayslipTable({ title, rows, tone }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#d8c38a] bg-white/60">
      <div className={`px-3 py-2 text-sm font-bold ${tone === "red" ? "bg-[#5a2525] text-white" : "bg-[#8a6b28] text-white"}`}>{title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-[#e4d6ad]">
          {rows.map((row) => (
            <tr key={row.label}>
              <td className="px-3 py-2 font-medium text-[#3f3421]">{row.label}</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums text-[#17130b]">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayslipTotal({ label, value, danger, highlight }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${highlight ? "border-[#b49245] bg-[#17130b] text-[#f3d98b]" : danger ? "border-[#d9a6a6] bg-[#fff0f0] text-[#6f2525]" : "border-[#d8c38a] bg-white/70 text-[#17130b]"}`}>
      <div className="text-xs font-semibold opacity-80">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{formatCurrency(value)}</div>
    </div>
  );
}

function PayrollEmployeeForm({ editingEmployee, form, language, onChange, onClose, onLanguageChange, onSave, driverImportOptions = [] }) {
  const t = LANG[language];
  const [errors, setErrors] = useState([]);
  const [employeeSourceMode, setEmployeeSourceMode] = useState(form.linked_account_type === "driver" ? "driver" : "manual");
  const activeDriverImportOptions = useMemo(
    () => (driverImportOptions || []).filter((driver) => driver.is_active !== false),
    [driverImportOptions],
  );
  const allowanceRows = useMemo(() => parseAllowanceRows(form.allowances_text), [form.allowances_text]);
  const update = (key, value) => onChange({ ...form, [key]: value });
  const updateAllowances = (rows) => update("allowances_text", buildAllowanceText(rows));
  const applyManualEmployeeMode = () => {
    setEmployeeSourceMode("manual");
    onChange({
      ...form,
      linked_account_type: "",
      linked_account_id: "",
      employee_type: form.employee_type === "driver" ? "staff" : form.employee_type,
    });
  };
  const applyDriverImport = (driverId) => {
    const driver = activeDriverImportOptions.find((item) => item.id === driverId);
    if (!driver) {
      onChange({ ...form, linked_account_type: "driver", linked_account_id: "" });
      return;
    }
    onChange({
      ...form,
      name: driver.name || form.name,
      phone: driver.phone || form.phone,
      employee_type: "driver",
      linked_account_type: "driver",
      linked_account_id: driver.id,
    });
  };
  const submit = () => {
    const validation = validatePayrollForm(form, t);
    setErrors(validation);
    if (validation.length > 0) return;
    onSave();
  };

  return (
    <div className="internal-payroll-modal-backdrop">
      <div className="internal-payroll-modal">
        <div className="shrink-0 flex items-start justify-between gap-4 border-b border-amber-300/20 bg-[rgba(10,10,10,0.96)] p-5 backdrop-blur-xl">
          <div>
            <h3 className="text-xl font-semibold text-white">{editingEmployee ? t.modalTitleEdit : t.modalTitleCreate}</h3>
            <p className="mt-1 text-sm text-slate-400">{t.modalSubtitle}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className="flex items-center gap-2 rounded-xl border border-amber-300/20 bg-black/30 px-2 py-1.5 text-xs text-slate-300">
              <span>{t.langLabel}</span>
              <select value={language} onChange={(event) => onLanguageChange(event.target.value)} className="border-0 bg-transparent text-[#f3d98b] outline-none">
                <option value="zh">中文</option>
                <option value="ja">日本語</option>
              </select>
            </label>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="internal-payroll-modal-scroll grid gap-5">
          {errors.length > 0 && (
            <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {errors.join(" / ")}
            </div>
          )}

          {!editingEmployee && (
            <PayrollSection title={t.sourceSetup}>
              <div className="sm:col-span-2 grid gap-3 lg:grid-cols-[1fr_1.4fr]">
                <div className="grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={applyManualEmployeeMode}
                    className={`rounded-xl border px-4 py-3 text-left transition ${employeeSourceMode === "manual" ? "border-amber-300/55 bg-amber-300/10 text-[#f3d98b]" : "border-white/10 bg-black/25 text-slate-300 hover:border-amber-300/25"}`}
                  >
                    <span className="block text-sm font-semibold">{t.manualEmployeeMode}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmployeeSourceMode("driver")}
                    className={`rounded-xl border px-4 py-3 text-left transition ${employeeSourceMode === "driver" ? "border-amber-300/55 bg-amber-300/10 text-[#f3d98b]" : "border-white/10 bg-black/25 text-slate-300 hover:border-amber-300/25"}`}
                  >
                    <span className="block text-sm font-semibold">{t.driverImportMode}</span>
                    <span className="mt-1 block text-xs text-slate-500">{t.driverImportHint}</span>
                  </button>
                </div>
                {employeeSourceMode === "driver" && (
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-slate-300">{t.selectDriver}</span>
                    <select
                      value={form.linked_account_id || ""}
                      onChange={(event) => applyDriverImport(event.target.value)}
                      className="payroll-field"
                    >
                      <option value="">{activeDriverImportOptions.length ? t.selectDriverPlaceholder : t.noActiveDrivers}</option>
                      {activeDriverImportOptions.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name}{driver.phone ? ` / ${driver.phone}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </PayrollSection>
          )}

          <PayrollSection title={t.basicInfo}>
            <PayrollInput label={`${t.name} *`} value={form.name} onChange={(value) => update("name", value)} />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-300">{t.employeeType}</span>
              <select value={form.employee_type} onChange={(event) => update("employee_type", event.target.value)} className="payroll-field">
                <option value="staff">{t.staff}</option>
                <option value="driver">{t.driver}</option>
                <option value="dispatcher">{t.dispatcher}</option>
                <option value="internal">{t.internal}</option>
                <option value="admin">{t.admin}</option>
              </select>
            </label>
            <PayrollInput label={t.phone} value={form.phone} onChange={(value) => update("phone", value)} />
            <PayrollInput label={t.employeeNo} value={form.employee_code} onChange={(value) => update("employee_code", value)} />
            <PayrollInput label={t.department} value={form.department} onChange={(value) => update("department", value)} />
          </PayrollSection>

          <PayrollSection title={t.salarySetup}>
            <PayrollMoneyInput label={`${t.baseSalary} *`} value={form.base_salary} onChange={(value) => update("base_salary", value)} />
            <PayrollMoneyInput label={t.standardMonthly} value={form.standard_monthly_remuneration} onChange={(value) => update("standard_monthly_remuneration", value)} hint={t.standardHint} />
          </PayrollSection>

          <PayrollSection title={t.taxInsurance}>
            <PayrollInput label={t.dependents} type="number" value={form.dependent_count} onChange={(value) => update("dependent_count", value)} />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-300">{t.taxCategory}</span>
              <select value={form.tax_category || "kou"} onChange={(event) => update("tax_category", event.target.value)} className="payroll-field">
                <option value="kou">{t.taxKou}</option>
                <option value="otsu">{t.taxOtsu}</option>
              </select>
              <span className="mt-1 block text-xs leading-5 text-slate-500">{t.taxCategoryHint}</span>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-300">{t.prefecture} *</span>
              <select value={form.social_insurance_prefecture || "chiba"} onChange={(event) => update("social_insurance_prefecture", event.target.value)} className="payroll-field">
                {PREFECTURES.map((prefecture) => (
                  <option key={prefecture.value} value={prefecture.value}>{prefecture.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-300">{t.employmentCategory}</span>
              <select value={form.employment_insurance_category || "general"} onChange={(event) => update("employment_insurance_category", event.target.value)} className="payroll-field">
                <option value="general">{t.generalBusiness}</option>
                <option value="transport">{t.transportBusiness}</option>
                <option value="construction">{t.constructionBusiness}</option>
              </select>
            </label>
            <SwitchField label={t.careInsurance} hint={t.careHint} checked={Boolean(form.care_insurance_applicable)} onChange={(checked) => update("care_insurance_applicable", checked)} />
            <SwitchField label={t.payrollTarget} hint={t.payrollTargetHint} checked={Boolean(form.is_active)} onChange={(checked) => update("is_active", checked)} />
          </PayrollSection>

          <PayrollSection title={t.allowanceNotes} wide>
            <div className="sm:col-span-2">
              <div className="overflow-hidden rounded-xl border border-amber-300/20">
                <div className="grid grid-cols-[1fr_180px_80px] gap-2 bg-black/30 px-3 py-2 text-xs font-semibold text-slate-400">
                  <span>{t.allowanceName}</span>
                  <span>{t.amount}</span>
                  <span className="text-right">{t.delete}</span>
                </div>
                <div className="divide-y divide-white/10">
                  {allowanceRows.map((row, index) => (
                    <div key={row.id || index} className="grid grid-cols-[1fr_180px_80px] gap-2 px-3 py-2">
                      <input
                        value={row.label}
                        onChange={(event) => {
                          const next = allowanceRows.map((item, idx) => idx === index ? { ...item, label: event.target.value } : item);
                          updateAllowances(next);
                        }}
                        className="payroll-field"
                        placeholder={language === "ja" ? "交通費" : "交通费"}
                      />
                      <input
                        value={formatCurrencyInput(row.amount)}
                        onChange={(event) => {
                          const next = allowanceRows.map((item, idx) => idx === index ? { ...item, amount: numberValue(event.target.value) } : item);
                          updateAllowances(next);
                        }}
                        className="payroll-field text-right"
                        inputMode="numeric"
                      />
                      <button
                        type="button"
                        onClick={() => updateAllowances(allowanceRows.filter((_, idx) => idx !== index))}
                        className="internal-graphite-button !px-2 !py-2"
                        aria-label={t.delete}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateAllowances([...allowanceRows, { id: `allowance-${Date.now()}`, label: "", amount: "" }])}
                className="internal-gold-button mt-3 !py-2"
              >
                <Plus className="h-4 w-4" />
                {t.addAllowance}
              </button>
            </div>
            <label className="sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-slate-300">{t.notes}</span>
              <textarea value={form.payroll_notes} onChange={(event) => update("payroll_notes", event.target.value)} className="payroll-field min-h-24" />
            </label>
          </PayrollSection>
        </div>

        <div className="internal-payroll-modal-actions">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={onClose} className="internal-graphite-button flex-1">{t.cancel}</button>
            <button type="button" onClick={submit} className="internal-gold-button flex-1">{t.save}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PayslipEditorModal({ form, loading, onChange, onClose, onSave, t }) {
  const updateBasic = (key, value) => onChange({ ...form, basic_info: { ...form.basic_info, [key]: value } });
  const updateAttendance = (key, value) => onChange({ ...form, attendance: { ...form.attendance, [key]: value } });
  const updateItem = (section, index, key, value) => {
    const rows = (form[section] || []).map((item, idx) => idx === index ? { ...item, [key]: value } : item);
    onChange({ ...form, [section]: rows });
  };
  const addItem = (section, taxable = true) => onChange({
    ...form,
    [section]: [...(form[section] || []), { label: "", amount: "", taxable, source: "manual_editor" }],
  });
  const removeItem = (section, index) => onChange({
    ...form,
    [section]: (form[section] || []).filter((_, idx) => idx !== index),
  });

  return (
    <div className="internal-payroll-modal-backdrop">
      <div className="internal-payroll-modal">
        <div className="shrink-0 flex items-start justify-between gap-4 border-b border-amber-300/20 bg-[rgba(10,10,10,0.96)] p-5 backdrop-blur-xl">
          <div>
            <h3 className="text-xl font-semibold text-white">{t.editPayslip}</h3>
            <p className="mt-1 text-sm text-slate-400">{form.basic_info?.employee_name || "-"}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="internal-payroll-modal-scroll grid gap-5">
          <PayrollSection title={t.payslipBasicInfo}>
            <PayrollInput label={t.employeeName} value={form.basic_info.employee_name} onChange={(value) => updateBasic("employee_name", value)} />
            <PayrollInput label={t.employeeNo} value={form.basic_info.employee_code} onChange={(value) => updateBasic("employee_code", value)} />
            <PayrollInput label={t.department} value={form.basic_info.department} onChange={(value) => updateBasic("department", value)} />
            <PayrollInput label={t.paymentMonth} value={form.basic_info.month} onChange={(value) => updateBasic("month", value)} />
            <PayrollInput label={t.paymentDate} value={form.basic_info.payment_date} onChange={(value) => updateBasic("payment_date", value)} />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-300">{t.taxCategory}</span>
              <select value={form.basic_info.tax_category || "kou"} onChange={(event) => updateBasic("tax_category", event.target.value)} className="payroll-field">
                <option value="kou">{t.taxKou}</option>
                <option value="otsu">{t.taxOtsu}</option>
              </select>
              <span className="mt-1 block text-xs leading-5 text-slate-500">{t.taxCategoryHint}</span>
            </label>
          </PayrollSection>

          <PayrollSection title="勤怠">
            <PayrollInput label={t.workDays} type="number" value={form.attendance.work_days} onChange={(value) => updateAttendance("work_days", value)} />
            <PayrollInput label={t.attendanceDays} type="number" value={form.attendance.attendance_days} onChange={(value) => updateAttendance("attendance_days", value)} />
            <PayrollInput label={t.absenceDays} type="number" value={form.attendance.absence_days} onChange={(value) => updateAttendance("absence_days", value)} />
            <PayrollInput label={t.workHours} type="number" value={form.attendance.work_hours} onChange={(value) => updateAttendance("work_hours", value)} />
            <PayrollInput label={t.overtimeHours} type="number" value={form.attendance.overtime_hours} onChange={(value) => updateAttendance("overtime_hours", value)} />
          </PayrollSection>

          <EditablePayslipItems
            title={t.earningsSection}
            rows={form.earnings || []}
            section="earnings"
            onAdd={() => addItem("earnings", true)}
            onRemove={removeItem}
            onUpdate={updateItem}
            t={t}
            taxable
          />
          <EditablePayslipItems
            title={t.deductionsSection}
            rows={form.deductions || []}
            section="deductions"
            onAdd={() => addItem("deductions", false)}
            onRemove={removeItem}
            onUpdate={updateItem}
            t={t}
          />

          <label>
            <span className="mb-1 block text-sm font-medium text-slate-300">{t.notes}</span>
            <textarea value={form.notes || ""} onChange={(event) => onChange({ ...form, notes: event.target.value })} className="payroll-field min-h-24" />
          </label>
        </div>
        <div className="internal-payroll-modal-actions">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={onClose} className="internal-graphite-button flex-1">{t.cancel}</button>
            <button type="button" onClick={onSave} disabled={loading} className="internal-gold-button flex-1">
              <Save className="h-4 w-4" />
              {t.savePayslip}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditablePayslipItems({ title, rows, section, onAdd, onRemove, onUpdate, t, taxable = false, disabled = false, light = false }) {
  return (
    <section className={light ? "border border-[#b7c0c6] bg-white/75" : "rounded-2xl border border-amber-300/20 bg-white/[0.035] p-4"}>
      <div className="mb-3 flex items-center justify-between">
        <h4 className={light ? "px-3 pt-3 text-sm font-bold text-[#24301f]" : "text-sm font-semibold text-[#f3d98b]"}>{title}</h4>
        <button type="button" onClick={onAdd} disabled={disabled} className={light ? "mr-3 mt-3 inline-flex items-center gap-1 rounded-md border border-[#8a6b28] px-2.5 py-1.5 text-xs font-bold text-[#6f571d] disabled:opacity-40" : "internal-gold-button !px-3 !py-2 !text-xs"}>
          <Plus className="h-3.5 w-3.5" />
          {section === "earnings" ? t.addEarning : t.addDeduction}
        </button>
      </div>
      <div className={light ? "overflow-x-auto border-t border-[#b7c0c6]" : "overflow-hidden rounded-xl border border-amber-300/20"}>
        <div className={`grid min-w-[520px] ${taxable ? "grid-cols-[1fr_150px_110px_56px]" : "grid-cols-[1fr_150px_56px]"} gap-2 ${light ? "bg-[#e8ece8] text-[#5f6970]" : "bg-black/30 text-slate-400"} px-3 py-2 text-xs font-semibold`}>
          <span>{t.allowanceName}</span>
          <span>{t.amount}</span>
          {taxable && <span>{t.taxable}</span>}
          <span className="text-right">{t.delete}</span>
        </div>
        <div className={light ? "divide-y divide-[#ccd2d5]" : "divide-y divide-white/10"}>
          {rows.map((row, index) => (
            <div key={`${section}-${index}`} className={`grid min-w-[520px] ${taxable ? "grid-cols-[1fr_150px_110px_56px]" : "grid-cols-[1fr_150px_56px]"} gap-2 px-3 py-2`}>
              <input value={row.label || ""} onChange={(event) => onUpdate(section, index, "label", event.target.value)} disabled={disabled} className={light ? "payroll-table-field" : "payroll-field"} />
              <input value={formatCurrencyInput(row.amount)} onChange={(event) => onUpdate(section, index, "amount", numberValue(event.target.value))} disabled={disabled} className={`${light ? "payroll-table-field" : "payroll-field"} text-right`} inputMode="numeric" />
              {taxable && (
                <label className={`flex items-center gap-2 text-xs ${light ? "text-[#4f5c64]" : "text-slate-300"}`}>
                  <input type="checkbox" checked={row.taxable !== false} onChange={(event) => onUpdate(section, index, "taxable", event.target.checked)} disabled={disabled} />
                  {row.taxable === false ? t.nonTaxable : t.taxable}
                </label>
              )}
              <button type="button" onClick={() => onRemove(section, index)} disabled={disabled} className={light ? "inline-flex items-center justify-center rounded-md border border-[#c8ced1] text-[#6c3333] disabled:opacity-30" : "internal-graphite-button !px-2 !py-2"} aria-label={t.delete}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PayrollSection({ title, children }) {
  return (
    <section className="rounded-2xl border border-amber-300/20 bg-white/[0.035] p-4">
      <h4 className="mb-4 text-sm font-semibold text-[#f3d98b]">{title}</h4>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function PayrollInput({ label, type = "text", value, onChange, hint, disabled = false, light = false }) {
  return (
    <label className="block">
      <span className={`mb-1 block ${light ? "text-xs font-bold text-[#6f5d38]" : "text-sm font-medium text-slate-300"}`}>{label}</span>
      <input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} disabled={disabled} className={light ? "payroll-table-field" : "payroll-field"} />
      {hint && <span className="mt-1 block text-xs leading-5 text-slate-500">{hint}</span>}
    </label>
  );
}

function PayrollMoneyInput({ label, value, onChange, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-300">{label}</span>
      <input
        value={formatCurrencyInput(value)}
        onChange={(event) => onChange(numberValue(event.target.value))}
        className="payroll-field text-right font-semibold tabular-nums"
        inputMode="numeric"
      />
      {hint && <span className="mt-1 block text-xs leading-5 text-slate-500">{hint}</span>}
    </label>
  );
}

function SwitchField({ label, hint, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-4 rounded-xl border border-amber-300/20 bg-black/20 p-3">
      <span>
        <span className="block text-sm font-semibold text-white">{label}</span>
        <span className="mt-1 block text-xs leading-5 text-slate-500">{hint}</span>
      </span>
      <span className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border transition ${checked ? "border-amber-300/45 bg-amber-300/25" : "border-white/15 bg-white/10"}`}>
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="sr-only" />
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-6" : "left-1"}`} />
      </span>
    </label>
  );
}
